import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import axios from "../../../utills/api";
import ReactApexChart from "react-apexcharts";

// Utility to resolve CSS variables
const getChartColorsArray = (colors) => {
  if (!colors) return [];
  try {
    const parsed = JSON.parse(colors);
    return parsed.map((value) => {
      const newValue = value.replace(" ", "");
      if (newValue.indexOf("--") !== -1) {
        const color = getComputedStyle(document.documentElement).getPropertyValue(
          newValue
        );
        return color ? color.trim() : newValue;
      } else {
        return newValue;
      }
    });
  } catch (e) {
    return [];
  }
};

// Donut chart component with total bookings inside
const BookingsDonutChart = ({ series, totalBookings, dataColors }) => {
  const colors = getChartColorsArray(dataColors);

  const options = {
    chart: {
      type: "donut",
      height: 250,
    },
    plotOptions: {
      pie: {
        donut: {
          size: "75%", // donut thickness
          labels: {
            show: true,
            name: { show: false },
            value: {
              show: true,
              fontSize: "24px",
              fontWeight: 700,
              color: "#000",
              offsetY: 0,
              formatter: () => totalBookings,
            },
            total: {
              show: true,
              label: "Total Bookings",
              fontSize: "14px",
              color: "#666",
              formatter: () => "",
            },
          },
        },
      },
    },
    stroke: { lineCap: "round", width: 2 },
    dataLabels: { enabled: false },
    legend: { show: false },
    colors: colors,
  };

  return (
    <div className="position-relative d-flex justify-content-center">
      <ReactApexChart
        options={options}
        series={series}
        type="donut"
        height={250}
        className="apex-charts"
      />

      {/* white shadow circle overlay */}
      <div
        className="position-absolute d-flex flex-column align-items-center justify-content-center"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "white",
          borderRadius: "50%",
          boxShadow: "0 0 12px rgba(0,0,0,0.15)",
          width: "100px",
          height: "100px",
        }}
      >
        <h4 className="fw-bold mb-0">{totalBookings}</h4>
        <small className="text-muted">Total Bookings</small>
      </div>
    </div>
  );
};

const ClosingDeals = () => {
  const [chartData, setChartData] = useState([0, 0, 0]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState("All Time");

  const fetchData = async () => {
    try {
      const [housekeepingRes, foodRes, spaRes, taxiRes] = await Promise.all([
        axios.get("/requests/guest"),
        axios.get("/food/order"),
        axios.get("/spa"),
        axios.get("/taxi-booking"),
      ]);

      const housekeeping = Array.isArray(housekeepingRes.data)
        ? housekeepingRes.data
        : [];
      const food = Array.isArray(foodRes.data) ? foodRes.data : [];
      const spa = Array.isArray(spaRes.data) ? spaRes.data : [];
      const taxi = Array.isArray(taxiRes.data) ? taxiRes.data : [];

      const total =
        housekeeping.length + food.length + spa.length + taxi.length;
      setTotalBookings(total);

      const completed =
        housekeeping.filter(
          (h) => h.status?.toLowerCase() === "completed"
        ).length +
        food.filter((f) => f.status?.toLowerCase() === "completed").length +
        spa.filter((s) => s.status?.toLowerCase() === "completed").length +
        taxi.filter((t) => t.status?.toLowerCase() === "completed").length;

      const received =
        housekeeping.filter(
          (h) =>
            h.status?.toLowerCase() === "pending" ||
            h.status?.toLowerCase() === "received"
        ).length +
        food.filter(
          (f) =>
            f.status?.toLowerCase() === "pending" ||
            f.status?.toLowerCase() === "received"
        ).length +
        spa.filter(
          (s) =>
            s.status?.toLowerCase() === "pending" ||
            s.status?.toLowerCase() === "received"
        ).length +
        taxi.filter(
          (t) =>
            t.status?.toLowerCase() === "pending" ||
            t.status?.toLowerCase() === "received"
        ).length;

      const ongoing = total - completed - received;
      setChartData([completed, ongoing, received]);
    } catch (err) {
      console.error("Error fetching booking status:", err);
      setChartData([0, 0, 0]);
      setTotalBookings(0);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onChangeChartPeriod = (pType) => {
    setSelectedMonth(pType);
    fetchData();
  };

  return (
    <React.Fragment>
      <Col xl={6} lg={6}>
        <Card className="card-height-100">
          <CardHeader className="align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">Bookings Status</h4>
            <div className="flex-shrink-0">
              <UncontrolledDropdown className="card-header-dropdown">
                <DropdownToggle
                  tag="a"
                  className="dropdown-btn text-muted"
                  role="button"
                >
                  {selectedMonth} <i className="mdi mdi-chevron-down ms-1"></i>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  <DropdownItem onClick={() => onChangeChartPeriod("All Time")}>
                    All Time
                  </DropdownItem>
                  <DropdownItem onClick={() => onChangeChartPeriod("Last 7 Days")}>
                    Last 7 Days
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => onChangeChartPeriod("Last 30 Days")}
                  >
                    Last 30 Days
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => onChangeChartPeriod("Last 90 Days")}
                  >
                    Last 90 Days
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </CardHeader>

          <CardBody>
            <div id="bookings-status" className="apex-charts" dir="ltr">
              <BookingsDonutChart
                series={chartData}
                totalBookings={totalBookings}
                dataColors='["--vz-success", "--vz-primary", "--vz-warning"]'
              />
            </div>

            {/* ✅ 3-column stats below donut */}
            <div className="d-flex justify-content-around align-items-center mt-4 text-center border-top pt-3">
              <div>
                <h3 className="mb-1 text-success fw-bold">
                  {chartData[0].toString().padStart(2, "0")}
                </h3>
                <small className="text-muted">Completed Bookings</small>
              </div>
              <div
                style={{
                  height: "40px",
                  width: "1px",
                  backgroundColor: "#ddd",
                }}
              ></div>
              <div>
                <h3 className="mb-1 text-primary fw-bold">
                  {chartData[1].toString().padStart(2, "0")}
                </h3>
                <small className="text-muted">Ongoing Bookings</small>
              </div>
              <div
                style={{
                  height: "40px",
                  width: "1px",
                  backgroundColor: "#ddd",
                }}
              ></div>
              <div>
                <h3 className="mb-1 text-warning fw-bold">
                  {chartData[2].toString().padStart(2, "0")}
                </h3>
                <small className="text-muted">Received Bookings</small>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default ClosingDeals;
